import "./styles.scss";

import { useRef, useState } from "react";
import classnames from "classnames";

import useClickOutside from "libs/helpers/useClickOutside";
import { getTypeIcon } from "./helpers";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as CheckIcon } from "assets/icons/new/check.svg";
import { ReactComponent as RemoveIcon } from "assets/icons/new/x.svg";

const mainClass = "selectable-pill";

const SelectablePill = ({
  data,
  icon,
  options,
  optionsDisabled,
  optionsFooter,
  isSelected,
  isCompletelyDisabled,
  isCompact,
  isDisabled,
  onClick,
  onChange,
}) => {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleOnClick = () => {
    if (isCompletelyDisabled) return;

    if (options?.length > 0) {
      setIsOpen((prevOpen) => !prevOpen);
    } else if (typeof onClick === "function") {
      onClick(data);
    }
  };

  const handleOnChange = (option) => {
    setIsOpen(false);

    if (typeof onChange === "function") onChange(option);
  };

  const handleFooterClick = (e) => {
    if (
      e.target.tagName.toLowerCase() === "button" ||
      e.target.closest("button")
    ) {
      setIsOpen(false);
    }
  };

  useClickOutside(ref, () => {
    setIsOpen(false);
  });

  const isInline = data?.type === "inline";
  const isPicker = data?.type === "picker";
  const isRemove = data?.type === "remove";

  return (
    <div
      ref={ref}
      className={classnames(mainClass, {
        [`${mainClass}--${data?.color}`]: !!data?.color,
        [`${mainClass}--${data?.type}`]: !!data?.type,
        [`${mainClass}--selected`]: isSelected,
        [`${mainClass}--disabled`]: isDisabled,
        [`${mainClass}--compact`]: isCompact,
        [`${mainClass}--completely-disabled`]: isCompletelyDisabled,
      })}
    >
      <button
        className={`${mainClass}__button`}
        type="button"
        disabled={
          isDisabled || (!(options?.length > 0) && !onClick && !onChange)
        }
        onClick={handleOnClick}
      >
        {!isInline && (
          <i className={classnames({ [icon]: !!icon, empty: isPicker })}>
            {!!icon ? getTypeIcon(icon) : null}
          </i>
        )}
        <em>{data?.label}</em>
        {(options?.length > 0 || isSelected || isRemove) &&
          !isCompletelyDisabled && (
            <span
              className={classnames({
                checkmark: !!isSelected && !isInline,
                remove: isRemove || isInline,
              })}
            >
              {isSelected && !isInline ? (
                <CheckIcon />
              ) : isRemove || isInline ? (
                <RemoveIcon />
              ) : (
                <ArrowIcon />
              )}
            </span>
          )}
      </button>
      {options?.length > 0 && isOpen && (
        <div className={`${mainClass}__options`}>
          {options
            .sort((a, b) => {
              const a_selected = a.value === data?.value ? 1 : -1;
              const b_selected = b.value === data?.value ? 1 : -1;

              return a_selected < b_selected ? 1 : -1;
            })
            .map((option) => (
              <SelectablePill
                key={option.value}
                data={option}
                icon={icon}
                isSelected={data?.value === option.value}
                isDisabled={optionsDisabled?.includes(option.value)}
                onClick={() => handleOnChange(option)}
              />
            ))}
          {optionsFooter && (
            <div
              className={`${mainClass}__options__footer`}
              onClick={handleFooterClick}
            >
              <p>{optionsFooter}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectablePill;
